import type {
  ReportFormat,
  ReportOrientation,
} from "@/components/records/report-generation-dialog/use-generate-report";

/**
 * The file choices an issues report is made with. Remembered per device, and
 * apart from the photos dialog's settings: the two dialogs offer different
 * things, and a photos choice should not silently become an issues default.
 * See [[issues-report-dialog.spec]] § Where the settings live.
 */
export interface IssuesReportSettings {
  format: ReportFormat;
  orientation: ReportOrientation;
}

export const DEFAULT_ISSUES_REPORT_SETTINGS: IssuesReportSettings = {
  format: "PDF",
  orientation: "PORTRAIT",
};

const STORAGE_KEY = "otus:issues-report-config";

const FORMATS: readonly ReportFormat[] = ["PDF", "PPTX"];
const ORIENTATIONS: readonly ReportOrientation[] = ["PORTRAIT", "LANDSCAPE"];

function pick<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T,
): T {
  return typeof value === "string" && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

/**
 * The last-used settings, field by field: an unknown or missing value falls
 * back to its default without discarding the others. Storage that is missing,
 * refused or corrupt yields the defaults.
 */
export function readIssuesReportSettings(): IssuesReportSettings {
  const defaults = DEFAULT_ISSUES_REPORT_SETTINGS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Record<string, unknown> | null;
    if (!parsed || typeof parsed !== "object") return defaults;
    return {
      format: pick(parsed.format, FORMATS, defaults.format),
      orientation: pick(parsed.orientation, ORIENTATIONS, defaults.orientation),
    };
  } catch {
    return defaults;
  }
}

export function saveIssuesReportSettings(settings: IssuesReportSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage unavailable (private mode, quota): the choice still drives this
    // report, it just isn't remembered for the next one.
  }
}
